import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { 
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from "../components/ui/table";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  AlertCircle, AlertTriangle, RefreshCw, Loader2, 
  RotateCcw, Download, ChevronLeft, ChevronRight
} from "lucide-react";
import { toast } from "sonner";

const DataQualityPage = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("cusips");
  
  // Data lists
  const [unresolvedCusips, setUnresolvedCusips] = useState([]);
  const [missingProfiles, setMissingProfiles] = useState([]);
  const [failedTasks, setFailedTasks] = useState([]);
  const [errors, setErrors] = useState([]);
  
  // Pagination
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const limit = 50;

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API}/data-quality/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchData = useCallback(async () => {
    try {
      let url = "";
      switch (activeTab) {
        case "cusips":
          url = `${API}/data-quality/unresolved-cusips?limit=${limit}&offset=${offset}`;
          break;
        case "profiles":
          url = `${API}/data-quality/missing-profiles?limit=${limit}&offset=${offset}`;
          break;
        case "failed":
          url = `${API}/data-quality/failed-tasks?limit=${limit}&offset=${offset}`;
          break;
        case "errors":
          url = `${API}/data-quality/errors?limit=${limit}&offset=${offset}`;
          break;
      }
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        setTotal(data.total || 0);
        
        switch (activeTab) {
          case "cusips":
            setUnresolvedCusips(data.cusips || []);
            break;
          case "profiles":
            setMissingProfiles(data.profiles || []);
            break;
          case "failed":
            setFailedTasks(data.tasks || []);
            break;
          case "errors":
            setErrors(data.errors || []);
            break;
        }
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
    }
  }, [token, activeTab, offset]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    setOffset(0);
    fetchData();
  }, [activeTab]);

  useEffect(() => {
    fetchData();
  }, [offset]);

  const requeueCusips = async () => {
    try {
      const response = await fetch(`${API}/data-quality/requeue-unresolved-cusips`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`Queued ${data.queued_count} CUSIPs for resolution`);
        fetchStats();
      }
    } catch (error) {
      toast.error("Failed to requeue CUSIPs");
    }
  };

  const requeueProfiles = async () => {
    try {
      const response = await fetch(`${API}/data-quality/requeue-missing-profiles`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`Queued ${data.queued_count} symbols for enrichment`);
        fetchStats();
      }
    } catch (error) {
      toast.error("Failed to requeue profiles");
    }
  };

  const retryAllFailed = async () => {
    try {
      const response = await fetch(`${API}/data-quality/retry-failed-tasks`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`Reset ${data.reset_count} failed tasks`);
        fetchStats();
        fetchData();
      }
    } catch (error) {
      toast.error("Failed to retry tasks");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="data-quality-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Data Quality</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Monitor and fix data quality issues
          </p>
        </div>
        <Button variant="outline" onClick={fetchStats} className="rounded-sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <QualityStatCard
          label="Unresolved CUSIPs"
          value={stats?.unresolved_cusips || 0}
          variant={stats?.unresolved_cusips > 0 ? "warning" : "success"}
        />
        <QualityStatCard
          label="Missing Sectors"
          value={stats?.missing_sectors || 0}
          variant={stats?.missing_sectors > 0 ? "warning" : "success"}
        />
        <QualityStatCard
          label="Failed Tasks"
          value={stats?.funds_with_repeated_failures || 0}
          variant={stats?.funds_with_repeated_failures > 0 ? "error" : "success"}
        />
        <QualityStatCard
          label="Enrichment Backlog"
          value={stats?.enrichment_backlog || 0}
          variant={stats?.enrichment_backlog > 0 ? "warning" : "success"}
        />
      </div>

      {/* Quick Actions */}
      <div className="flex items-center gap-2">
        <Button variant="outline" onClick={requeueCusips} className="rounded-sm">
          <RotateCcw className="w-4 h-4 mr-2" />
          Requeue Unresolved CUSIPs
        </Button>
        <Button variant="outline" onClick={requeueProfiles} className="rounded-sm">
          <RotateCcw className="w-4 h-4 mr-2" />
          Requeue Missing Profiles
        </Button>
        <Button variant="outline" onClick={retryAllFailed} className="rounded-sm">
          <RotateCcw className="w-4 h-4 mr-2" />
          Retry All Failed Tasks
        </Button>
      </div>

      {/* Data Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-muted rounded-sm">
          <TabsTrigger value="cusips" className="rounded-sm">
            Unresolved CUSIPs ({stats?.unresolved_cusips || 0})
          </TabsTrigger>
          <TabsTrigger value="profiles" className="rounded-sm">
            Missing Profiles ({stats?.missing_sectors || 0})
          </TabsTrigger>
          <TabsTrigger value="failed" className="rounded-sm">
            Failed Tasks
          </TabsTrigger>
          <TabsTrigger value="errors" className="rounded-sm">
            Recent Errors
          </TabsTrigger>
        </TabsList>

        <TabsContent value="cusips" className="mt-4">
          <Card className="bg-card border-border rounded-sm">
            <CardContent className="p-0">
              <Table className="data-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>CUSIP</TableHead>
                    <TableHead>Symbol</TableHead>
                    <TableHead>Company Name</TableHead>
                    <TableHead>Last Updated</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {unresolvedCusips.map((c, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-mono">{c.holding_cusip_norm}</TableCell>
                      <TableCell className="text-muted-foreground">{c.cusip_symbol || "—"}</TableCell>
                      <TableCell className="text-muted-foreground">{c.cusip_company_name || "—"}</TableCell>
                      <TableCell className="text-sm">{c.updated_at ? new Date(c.updated_at).toLocaleString() : "—"}</TableCell>
                    </TableRow>
                  ))}
                  {unresolvedCusips.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                        No unresolved CUSIPs
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="profiles" className="mt-4">
          <Card className="bg-card border-border rounded-sm">
            <CardContent className="p-0">
              <Table className="data-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Symbol</TableHead>
                    <TableHead>Sector</TableHead>
                    <TableHead>Industry</TableHead>
                    <TableHead>Last Updated</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {missingProfiles.map((p, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-mono">{p.symbol}</TableCell>
                      <TableCell className="text-muted-foreground">{p.company_sector || "—"}</TableCell>
                      <TableCell className="text-muted-foreground">{p.company_industry || "—"}</TableCell>
                      <TableCell className="text-sm">{p.updated_at ? new Date(p.updated_at).toLocaleString() : "—"}</TableCell>
                    </TableRow>
                  ))}
                  {missingProfiles.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                        No missing profiles
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="failed" className="mt-4">
          <Card className="bg-card border-border rounded-sm">
            <CardContent className="p-0">
              <Table className="data-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>CIK</TableHead>
                    <TableHead>Quarter</TableHead>
                    <TableHead>Retries</TableHead>
                    <TableHead>Last Error</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {failedTasks.map((t, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-mono">{t.cik}</TableCell>
                      <TableCell className="font-mono">{t.year} Q{t.quarter}</TableCell>
                      <TableCell className="font-mono">{t.retries || 0}</TableCell>
                      <TableCell className="text-sm text-red-400 max-w-[300px] truncate">{t.last_error || "—"}</TableCell>
                    </TableRow>
                  ))}
                  {failedTasks.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                        No failed tasks
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="errors" className="mt-4">
          <Card className="bg-card border-border rounded-sm">
            <CardContent className="p-0">
              <Table className="data-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Time</TableHead>
                    <TableHead>Stage</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Message</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {errors.map((e, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-sm">{new Date(e.created_at).toLocaleString()}</TableCell>
                      <TableCell className="font-mono text-xs">{e.stage || "—"}</TableCell>
                      <TableCell>
                        <Badge className="status-failed rounded-sm text-xs">{e.error_type}</Badge>
                      </TableCell>
                      <TableCell className="text-sm text-red-400 max-w-[300px] truncate">{e.message}</TableCell>
                    </TableRow>
                  ))}
                  {errors.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                        No recent errors
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Showing {offset + 1} - {Math.min(offset + limit, total)} of {total}
        </p>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setOffset(Math.max(0, offset - limit))}
            disabled={offset === 0}
            className="rounded-sm"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setOffset(offset + limit)}
            disabled={offset + limit >= total}
            className="rounded-sm"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

const QualityStatCard = ({ label, value, variant = "default" }) => {
  const variants = {
    default: "text-foreground",
    success: "text-green-400",
    warning: "text-yellow-400",
    error: "text-red-400",
  };

  const icons = {
    default: null,
    success: null,
    warning: <AlertTriangle className="w-5 h-5" />,
    error: <AlertCircle className="w-5 h-5" />,
  };

  return (
    <Card className="bg-card border-border rounded-sm">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="label-xs">{label}</p>
            <p className={`stat-value ${variants[variant]}`}>{value.toLocaleString()}</p>
          </div>
          {icons[variant] && <span className={variants[variant]}>{icons[variant]}</span>}
        </div>
      </CardContent>
    </Card>
  );
};

export default DataQualityPage;
